import gettext from '@/utils/translationInjector.js';
const { $gettext } = gettext;
import { mapState, mapMutations, mapActions } from 'vuex';

import EventBus from '@/elements/eventBus.js';
import { MFA_LOGIN_CODES, EMAIL_CODE, MFA_SAVE_STATE_CODES, CAPTCHA_CODE } from '@/utils/cons';

const NOTIFY_ROUTES = ['/job/stop'];

export default {
  name: 'GlobalErrorMixin',
  data() {
    return {
      permissionRulesTree: {
        passwordPage: {
          checkPermissions: this.checkPasswordPermissions,
          rules: {
            404: {
              // object not found
              20: () => {
                this.setErrorPageKey('errorPasswordVisited');
              },
            },
          },
        },
        downloadPage: {
          checkPermissions: this.checkDownloadPermissions,
          rules: {
            403: {
              // wrong credentials
              50: () => {
                this.setLoginError('wrongCredentials');
              },
              // expired share
              60: () => {
                this.setErrorPageKey('errorDownloadExpired');
              },
              // deactivated user
              70: () => {
                this.setLoginError('wrongLogin');
              },
              // not released share
              81: () => {
                this.setErrorPageKey('errorDownloadNotReleased');
              },
            },
            404: {
              // object not found
              20: () => {
                this.setErrorPageKey('errorNotFound');
              },
              // revoked share
              80: () => {
                this.setErrorPageKey('errorDownloadRevoked');
              },
            },
          },
        },
        main: {
          checkPermissions: this.checkMainPermissions,
          rules: {
            403: {
              70: () => {
                this.setLoginError('cantLogin');
              },
            },
            404: {
              // suspended account
              80: () => {
                this.getAccountMetadata();
              },
              // user deleted
              90: () => {
                this.setLoginError('cantLogin');
              },
            },
          },
        },
        keyRequestPage: {
          checkPermissions: this.checkKeyRequestPermissions,
          rules: {
            403: {
              // wrong credentials
              50: () => {
                this.setLoginError('cantLogin');
              },
              // disabled pgp for account
              51: () => {
                this.setErrorPageKey('errorNoPGP');
              },
            },
            404: {
              20: () => {
                this.setErrorPageKey('errorKeyRequestNotFound');
              },
            },
          },
        },
        msft: {
          checkPermissions: this.checkMsftPermission,
          rules: {},
        },
        returnPage: {
          checkPermissions: this.checkReturnPermissions,
          rules: {
            403: {
              // wrong credentials
              50: () => {
                this.setLoginError('wrongCredentials');
              },
              // expired share
              60: () => {
                this.setErrorPageKey('errorReturnExpired');
              },
              // deactivate user
              70: () => {
                this.setLoginError('wrongLogin');
              },
              // not released share
              81: () => {
                this.setErrorPageKey('errorReturnNotReleased');
              },
            },
            404: {
              // object not found
              20: () => {
                this.setErrorPageKey('errorNotFound');
              },
              // revoked share
              80: () => {
                this.setErrorPageKey('errorReturnRevoked');
              },
            },
          },
        },
      },
    };
  },
  computed: {
    ...mapState({
      currentRulesKey: (state) => state.currentRulesKey,
    }),
    currentPermissionRules() {
      return this.permissionRulesTree[this.currentRulesKey];
    },
  },
  created() {
    EventBus.$on('handle-error', this.handleErrorFromRequest);
  },
  methods: {
    ...mapMutations({
      setMfaCode: 'auth/setMfaCode',
      setIsForcedLogin: 'auth/setIsForcedLogin',
      setDelayedCall: 'setDelayedCall',
      setCaptchaIP: 'auth/setCaptchaIP',
      setErrorPageKey: 'setErrorPageKey',
      setIsMsftAllowed: 'msft/setIsMsftAllowed',
      setLoginError: 'auth/setLoginError',
    }),
    ...mapActions({
      logout: 'logout',
      getDownloadPageMetadata: 'downloadPage/getMetadata',
      getProfile: 'profile/getProfile',
      getAccountMetadata: 'getAccountMetadata',
      getKeyRequestMetadata: 'keyRequestPage/getMetadata',
      getReturnMetadata: 'returnPage/getMetadata',
      getPasswordsMetadata: 'passwordPage/getMetadata',
    }),
    checkIfGlobalError({ response: { status, data = {} }, options = {} }) {
      if (status === 401) {
        const { code, details } = data;

        if (EMAIL_CODE === code) {
          this.setMfaCode(code);
          this.logout('timeout');
          return true;
        }
        if (MFA_LOGIN_CODES.includes(code)) {
          this.setMfaCode(code);
          if (MFA_SAVE_STATE_CODES.includes(code)) {
            this.setIsForcedLogin(true);
            this.setDelayedCall(options.args);
          }
          return true;
        }

        if (code === CAPTCHA_CODE) {
          this.setCaptchaIP(details.ip);
          this.setMfaCode(CAPTCHA_CODE);
          return true;
        }
        return false;
      }

      if (status === 413) {
        this.$notify({
          text: this.$gettext('Request entity too large'),
          type: 'error',
        });
        return true;
      }

      if (status === 500) {
        this.$notify({
          text: this.$gettext('Something went wrong'),
          type: 'error',
        });
        return true;
      }

      if (status === 502) {
        this.setErrorPageKey('errorBadGateway');
        return true;
      }

      return false;
    },
    handleErrorFromRequest({ response, options = {} }) {
      if (response) {
        const isGlobalError = this.checkIfGlobalError({ response, options });
        if (!isGlobalError) {
          let isForceNotify = false;
          const url = response.config?.url;
          if (url) {
            isForceNotify = NOTIFY_ROUTES.includes(url);
          }
          const status = response.status;
          const code = response.data?.code;
          if (status && code) {
            const permissionErrorAction = this.currentPermissionRules.rules[status]?.[code];
            if (permissionErrorAction && !isForceNotify) {
              permissionErrorAction();
              return;
            }
          }

          this.showNotification(response, options);
        }
      }
    },
    showNotification(response, options) {
      this.$notify({
        title: options.errorMsg || response.data?.msg || '',
        text: options.text || response.data?.details || '',
        type: 'error',
        ...(options.duration && { duration: options.duration }),
      });
    },
    async checkMainPermissions() {
      try {
        return await this.getProfile();
      } catch (error) {
        if (error?.response?.status === 404) {
          this.$notify({
            text: $gettext("You can't access this account."),
            type: 'error',
          });
        }
      }
    },
    //Permissions for Download Page
    checkDownloadPermissions() {
      return this.getDownloadPageMetadata(this.$route.params.id);
    },
    //Permissions for Set/Reset Password Page
    checkPasswordPermissions() {
      return this.getPasswordsMetadata(this.$route.params.id);
    },
    //Permissions for Return Files Page
    checkReturnPermissions() {
      return this.getReturnMetadata(this.$route.params.id);
    },
    //Permissions for Key Request Page
    checkKeyRequestPermissions() {
      return this.getKeyRequestMetadata(this.$route.params.id);
    },
    //Permissions for MSFT Page
    async checkMsftPermission() {
      try {
        const data = await this.getProfile();
        this.setIsMsftAllowed(true);
        return data;
      } catch (error) {
        this.setIsMsftAllowed(false);
        throw error;
      }
    },
  },
};
